import { MenuItemInfo } from '@tableau/widgets';
import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import * as _ from 'underscore';

import { ButtonControlledChildComponentProps } from '../../../common/react/components/ButtonControlledComponent';
import {
  ButtonControlledFlyoutListMenu,
  ButtonControlledFlyoutListMenuOwnProps,
  ButtonControlledFlyoutListMenuProps,
} from '../../../common/react/components/ButtonControlledFlyoutListMenu';
import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { requestAvailableActionsForEntity } from '../../actions/spaces/AvailableActionsForEntityActions';
import { selectAvailableActionsForEntity } from '../../selectors/EntityActionsSelectors';
import { AppState } from '../../state/AppState';
import { AvailableActions } from '../../state/AvailableActionsForEntityState';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { AllActionsAllowed, IEntityAction } from './EntityActions/IEntityAction';

export type EntityAction = IEntityAction<IExplorableItemTypeInfo, any>;

export interface ActionsMenuPanelStateProps {
  readonly menuItems: ButtonControlledFlyoutListMenuOwnProps['menuItems'];
}

export interface ActionsMenuPanelDispatchProps {
  readonly onComponentDidMount: ButtonControlledFlyoutListMenuOwnProps['onComponentDidMount'];
  readonly onMenuItemSelected: ButtonControlledFlyoutListMenuOwnProps['onMenuItemSelected'];
}

export interface ActionsMenuPanelOwnProps extends Pick<ButtonControlledFlyoutListMenuProps, 'id' | 'role'>{
  readonly entityActionSections: EntityAction[][];
  readonly targetItems: IExplorableItemTypeInfo[];
  readonly availableActions?: AvailableActions|undefined;
}


function buildMenuItems(entityActionSections: EntityAction[][],
                        targetItems: IExplorableItemTypeInfo[],
                        availableActions: AvailableActions | undefined,
                        state: AppState
): MenuItemInfo[] {
  const visibleActionSections = entityActionSections.map(section => section.filter(a => a.isVisible(targetItems, state, availableActions)))
      .filter(section => !_(section).isEmpty());

  const menuItems: MenuItemInfo[] = [];
  for (let i = 0; i < visibleActionSections.length; i++) {
    const section = visibleActionSections[i];
    menuItems.push(...section.filter(m => !m.parentName).map(m => {
      return {
        value: m.name,
        displayValue: m.translateName(targetItems, state),
        disabled: !m.isClickable(availableActions, targetItems, state),
        checked:  m.isChecked? m.isChecked(availableActions, targetItems, state) : false,
        menuItemInfos: (() => {
          const subMenuItems: MenuItemInfo[] = [];
          subMenuItems.push(...section.filter(subm => subm.parentName === m.name).map(subm =>
            ({
              value: subm.name,
              displayValue: subm.translateName(targetItems, state),
              disabled: !subm.isClickable(availableActions, targetItems, state),
              checked: subm.isChecked? subm.isChecked(availableActions, targetItems, state): false,
            })
          ));
          return subMenuItems.length > 0 ? subMenuItems: undefined;
        })()
      };
    }));

    if (i < visibleActionSections.length - 1) {
      menuItems.push({ isDivider: true });
    }
  }

  return menuItems;
}

export const mapStateToProps: MapStateToProps<ActionsMenuPanelStateProps, ActionsMenuPanelOwnProps> =
  (state: AppState, ownProps: ActionsMenuPanelOwnProps) => {

    let availableActions;
    if (ownProps.targetItems.length === 1) {
      availableActions = selectAvailableActionsForEntity(state, ownProps.targetItems[0]);
    } else {
      availableActions = AllActionsAllowed; // Replace this with the actual permissions in story 752767
    }

    return ({
      menuItems: buildMenuItems(ownProps.entityActionSections, ownProps.targetItems, availableActions, state),
    });
  };

export const mapDispatchToProps: MapDispatchToProps<ActionsMenuPanelDispatchProps, ActionsMenuPanelOwnProps> =
  (dispatch: Dispatch<AppState>, ownProps: ActionsMenuPanelOwnProps) => ({
    onComponentDidMount: () => {
      if (ownProps.targetItems.length === 1) {
        // TODO: Make this dispatch less noisy (it shouldn't have to call the API every time the user toggles the menu)
        const item = ownProps.targetItems[0];
        dispatch(requestAvailableActionsForEntity(item));
      }
    },
    onMenuItemSelected: (menuItemValue: string) => {
      const entityActions: EntityAction[] = _.flatten(ownProps.entityActionSections);
      const entityAction: EntityAction = _.find(entityActions, a => menuItemValue === a.name);
      const reduxAction = entityAction.createReduxAction(ownProps.targetItems);
      dispatch(reduxAction);
    },
  });

/**
 * This component takes an item and a global list of actions, and combines them with information from
 * the Redux store to produce menu items and handlers that can be passed into the FlyoutListMenuWidget.
 */
export const ConnectedActionsMenuPanel: React.ComponentType<ActionsMenuPanelOwnProps & ButtonControlledChildComponentProps> =
    connect(mapStateToProps, mapDispatchToProps)(ButtonControlledFlyoutListMenu);
